<template>
  <div class="space-y-4">
    <div class="grid max-w-full gap-4 md:grid-cols-2">
      <div class="min-w-0 rounded-md border border-obsidian-200 bg-white p-3">
        <div class="mb-2 text-xs font-semibold uppercase text-obsidian-600">
          {{ t('allocatedInvoices') }}
        </div>
        <div
          v-if="entry.invoiceAllocations?.length"
          class="grid grid-cols-[auto_auto_auto] gap-x-3 gap-y-2 text-sm"
        >
          <div class="font-medium text-obsidian-600">{{ t('amount') }}</div>
          <div class="font-medium text-obsidian-600">{{ t('invoice') }}</div>
          <div class="font-medium text-obsidian-600">{{ t('customerName') }}</div>
          <template v-for="allocatedInvoice in entry.invoiceAllocations" :key="allocatedInvoice.id">
            <div>
              <span v-currency="(allocatedInvoice.amount ?? 0) / 100" />
              /
              <span v-currency="getInvoiceTotalAmountInCents(allocatedInvoice) / 100" />
            </div>
            <div>
              <router-link
                v-if="allocatedInvoice.invoice?.id"
                :to="{ name: 'invoice', params: { id: allocatedInvoice.invoice.id } }"
                class="block min-w-0 truncate text-blue-700 underline"
              >
                {{ allocatedInvoice.invoice?.completeInvoiceNumber ?? '-' }} ({{
                  allocatedInvoice.invoice?.invoiceDate
                    ? formatDate(allocatedInvoice.invoice.invoiceDate, dateFormat)
                    : '-'
                }})
              </router-link>
              <span v-else>-</span>
            </div>
            <div>{{ getInvoiceCustomerName(allocatedInvoice) }}</div>
          </template>
        </div>
        <div v-else class="text-sm text-obsidian-500">
          {{ t('noAllocatedInvoices') }}
        </div>
      </div>
      <div class="min-w-0 rounded-md border border-obsidian-200 bg-white p-3">
        <div class="mb-2 text-xs font-semibold uppercase text-obsidian-600">
          {{ t('allocatedPayments') }}
        </div>
        <div
          v-if="entry.paymentAllocations?.length"
          class="grid grid-cols-[auto_auto_auto] gap-x-3 gap-y-2 text-sm"
        >
          <div class="font-medium text-obsidian-600">{{ t('amount') }}</div>
          <div class="font-medium text-obsidian-600">{{ t('payment') }}</div>
          <div class="font-medium text-obsidian-600">{{ t('associated') }}</div>
          <template v-for="allocatedPayment in entry.paymentAllocations" :key="allocatedPayment.id">
            <div>
              <span v-currency="(allocatedPayment.amount ?? 0) / 100" />
              /
              <span v-currency="allocatedPayment.payment?.amount ?? 0" />
            </div>
            <div class="min-w-0 truncate">
              {{ allocatedPayment.payment?.referenceId || '-' }},
              {{ t(`paymentTypes.${allocatedPayment.payment?.paymentType}`) }} ({{
                allocatedPayment.payment?.date
                  ? formatDate(allocatedPayment.payment.date, dateFormat)
                  : '-'
              }})
            </div>
            <div>
              <router-link
                v-if="getAssociatedPaymentSource(allocatedPayment).to"
                :to="getAssociatedPaymentSource(allocatedPayment).to!"
                class="block min-w-0 truncate text-blue-700 underline"
              >
                {{ getAssociatedPaymentSource(allocatedPayment).label }}
              </router-link>
              <span v-else>{{ getAssociatedPaymentSource(allocatedPayment).label }}</span>
            </div>
          </template>
        </div>
        <div v-else class="text-sm text-obsidian-500">
          {{ t('noAllocatedPayments') }}
        </div>
      </div>
    </div>
    <div
      v-if="entry.nettingAllocations?.length"
      class="min-w-0 rounded-md border border-obsidian-200 bg-white p-3"
    >
      <div class="mb-2 text-xs font-semibold uppercase text-obsidian-600">
        {{ t('nettings') }}
      </div>
      <div class="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-2 text-sm">
        <div class="font-medium text-obsidian-600">{{ t('nettingId') }}</div>
        <div class="font-medium text-obsidian-600">{{ t('amount') }}</div>
        <div class="font-medium text-obsidian-600">{{ t('type') }}</div>
        <div class="font-medium text-obsidian-600">{{ t('description') }}</div>
        <template v-for="nettingAllocation in entry.nettingAllocations" :key="nettingAllocation.id">
          <div class="font-mono text-xs text-obsidian-500">
            {{ nettingAllocation.netting?.id?.slice(0, 8) }}
          </div>
          <div>
            <span v-currency="(nettingAllocation.amount ?? 0) / 100" />
          </div>
          <div class="min-w-0 truncate">
            {{ t(`nettingTypes.${nettingAllocation.netting?.type}`) }}
          </div>
          <div class="min-w-0 truncate">
            {{ nettingAllocation.netting?.description || '-' }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useDateFormat } from '@/hooks/use-date-format.ts';
import type { PaymentAllocation, LedgerEntry } from '@/entities/ledger/ledger-entry.entity';
import type { InvoiceAllocation } from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';

defineProps<{
  entry: LedgerEntry;
}>();

const dateFormat = useDateFormat();
const { t } = useI18n();
const ability = useAbility();

const getInvoiceTotalAmountInCents = (allocatedInvoice: InvoiceAllocation) => {
  return allocatedInvoice.invoice.totalPositionSum * 100;
};

const getInvoiceCustomerName = (allocatedInvoice: InvoiceAllocation) => {
  const customer = allocatedInvoice.invoice?.customer;
  if (customer) {
    const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    if (fullName) return fullName;
  }
  const receiverData = allocatedInvoice.invoice?.receiverData;
  if (receiverData?.company) return receiverData.company;
  const receiverName = [receiverData?.firstName, receiverData?.lastName].filter(Boolean).join(' ');
  return receiverName || '-';
};

const getAssociatedPaymentSource = (allocatedPayment: PaymentAllocation) => {
  const invoiceId = allocatedPayment.payment?.invoice?.id;
  const invoiceNumber = allocatedPayment.payment?.invoice?.completeInvoiceNumber;
  if (invoiceNumber && invoiceId) {
    return {
      label: `IN ${invoiceNumber}`,
      to: {
        name: 'invoice',
        params: { id: invoiceId },
        query: allocatedPayment.payment?.id
          ? { paymentId: allocatedPayment.payment.id }
          : undefined,
      },
    };
  }

  const bookingId = allocatedPayment.payment?.booking?.id;
  const bookingNumber = allocatedPayment.payment?.booking?.bookingNumber;
  if (bookingNumber && bookingId) {
    return {
      label: `B ${bookingNumber}`,
      to: {
        name: 'bookingEdit',
        params: { id: bookingId },
        query: allocatedPayment.payment?.id
          ? { paymentId: allocatedPayment.payment.id }
          : undefined,
      },
    };
  }

  if (allocatedPayment.payment?.id && ability.can('get', 'Payment')) {
    return {
      label: t('payment'),
      to: { name: 'payment', params: { id: allocatedPayment.payment.id } },
    };
  }

  return {
    label: '-',
    to: undefined,
  };
};
</script>

<i18n lang="json">
{
  "en": {
    "amount": "Amount",
    "allocatedInvoices": "Allocated Invoices",
    "allocatedPayments": "Allocated Payments",
    "nettings": "Nettings",
    "invoice": "Invoice",
    "payment": "Payment",
    "associated": "Associated",
    "type": "Type",
    "description": "Description",
    "customerName": "Customer Name",
    "noAllocatedInvoices": "No allocated invoices",
    "noAllocatedPayments": "No allocated payments",
    "nettingId": "ID"
  },
  "de": {
    "amount": "Betrag",
    "allocatedInvoices": "Zugeordnete Rechnungen",
    "allocatedPayments": "Zugeordnete Zahlungen",
    "nettings": "Verrechnungen",
    "invoice": "Rechnung",
    "payment": "Zahlung",
    "associated": "Gehört zu",
    "type": "Typ",
    "description": "Beschreibung",
    "customerName": "Kundenname",
    "noAllocatedInvoices": "Keine zugeordneten Rechnungen",
    "noAllocatedPayments": "Keine zugeordneten Zahlungen",
    "nettingId": "ID"
  },
  "es": {
    "amount": "Importe",
    "allocatedInvoices": "Facturas imputadas",
    "allocatedPayments": "Pagos imputados",
    "nettings": "Compensaciones",
    "invoice": "Factura",
    "payment": "Pago",
    "associated": "Relacionado con",
    "type": "Tipo",
    "description": "Descripción",
    "customerName": "Nombre del cliente",
    "noAllocatedInvoices": "Sin facturas imputadas",
    "noAllocatedPayments": "Sin pagos imputados",
    "nettingId": "ID"
  }
}
</i18n>
